import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { formatErrorUserSafe, isRlsDenial } from "../lib/errors";
import { personKeys, usePerson } from "../queries/persons";
import { EnrichmentPanel } from "../features/enrichment/EnrichmentPanel";
import type { DetailRouteContext } from "../hooks/useDetailRoute";

interface OrgEnrichmentRow {
  id: string;
  enrichment_data: Record<string, unknown> | null;
  enriched_at: string | null;
}

function NotTracked() {
  return (
    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
      Enrichment not tracked for this entity.
    </Typography>
  );
}

function Loading() {
  return (
    <Box display="flex" justifyContent="center" py={2}>
      <CircularProgress size={20} />
    </Box>
  );
}

function ContactEnrichment({ id }: { id: string }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = usePerson(id);
  if (isLoading) return <Loading />;
  // SPEC manna-web-ui §3.13: collapse RLS denials into the neutral
  // not-tracked state so we don't leak permission boundaries.
  if (isError && isRlsDenial(error)) return <NotTracked />;
  if (isError) return <Alert severity="error">{formatErrorUserSafe(error)}</Alert>;
  if (!data) return <NotTracked />;
  return (
    <EnrichmentPanel
      entityType="person"
      entityId={data.id}
      enrichmentData={data.enrichment_data}
      enrichedAt={data.enriched_at}
      onRefresh={() => qc.invalidateQueries({ queryKey: personKeys.detail(data.id) })}
    />
  );
}

function OrganizationEnrichment({ id }: { id: string }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["organization", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manna_organizations_view")
        .select("id, enrichment_data, enriched_at")
        .eq("id", id)
        .maybeSingle();
      if (error) throw error;
      return data as OrgEnrichmentRow | null;
    },
  });
  if (isLoading) return <Loading />;
  // SPEC manna-web-ui §3.13: collapse RLS denials into the neutral
  // not-tracked state so we don't leak permission boundaries.
  if (isError && isRlsDenial(error)) return <NotTracked />;
  if (isError) return <Alert severity="error">{formatErrorUserSafe(error)}</Alert>;
  if (!data) return <NotTracked />;
  return (
    <EnrichmentPanel
      entityType="organization"
      entityId={data.id}
      enrichmentData={data.enrichment_data}
      enrichedAt={data.enriched_at}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["organization", data.id] })}
    />
  );
}

export function RelatedEnrichmentSlot({ ctx }: { ctx: DetailRouteContext }) {
  if (ctx.kind === "contact") return <ContactEnrichment id={ctx.id} />;
  if (ctx.kind === "organization") return <OrganizationEnrichment id={ctx.id} />;
  return <NotTracked />;
}
